import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentForm } from '../components/PaymentForm';
import { BankAccountSetup } from '../components/BankAccountSetup';
import { useAuth } from '../contexts/AuthContext';
import { STRIPE_PUBLISHABLE_KEY } from '../utils/env';
import { PaymentIntent } from '@stripe/stripe-js';
import { analytics, getSessionId } from '../utils/analytics';
import { ENDPOINTS } from '../config';
import { trackEvent } from '../utils/analytics';
import { checkoutSourceFor } from '../utils/checkoutSource';
import { SUMMER_CAMP_POLICY, isWithinCampNoRefundWindow, isSummerCampProgram } from '../utils/summerCampPolicy';
import { WalletTosGate } from '../components/WalletTosGate';
import { PaymentMethodIcon } from '../components/CardBrandIcon';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { FallbackMethodPicker, type FallbackOption } from '../components/FallbackMethodPicker';
import { useLabels } from '../config/useLabels';
import { useLandingBrand } from '../hooks/useLandingBrand';
import { calculateProcessingFee, processingFeeLabel, savedCardLabel } from '../utils/stripeFee';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';

interface ExtendedPaymentIntent extends PaymentIntent {
  payment_method_details?: {
    card?: {
      brand: string;
      last4: string;
    };
  };
  charges?: {
    data: Array<{
      receipt_url: string;
    }>;
  };
}

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface Invoice {
  id: number;
  total_amount: number;
  amount_paid: number;
  due_date: string;
  period_start: string;
  period_end: string;
  status: string;
  memo_code: string;
  is_prorated: boolean;
  enrollment: {
    id: number;
    student: {
      first_name: string;
      last_name: string;
    };
    program: {
      name: string;
      billing_type: string;
      type?: string;
      tags?: string[] | null;
      brand_key?: string;
    };
  };
}

interface ApplyCouponResponseRow {
  success: boolean;
  error: string | null;
  discount_amount: number;
  coupon_id: string;
  code: string;
  coupon_type: 'percentage' | 'fixed_amount';
  coupon_value: number;
  used_count: number;
  usage_limit: number | null;
  expires_at: string | null;
}

interface RemoveCouponResponseRow {
  success: boolean;
  error: string | null;
}

interface AvailableCouponTemplateInfo {
  name?: string | null;
  description?: string | null;
  active?: boolean | null;
}

interface AvailableCoupon {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  usage_limit: number | null;
  used_count: number;
  expires_at: string | null;
  applicable_invoice_id: string | null;
  template?: AvailableCouponTemplateInfo | null;
}

interface AppliedCouponServerRow {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  usage_limit: number | null;
  used_count: number;
  expires_at: string | null;
  template?: AvailableCouponTemplateInfo | null;
}

interface WalletBalanceTotals {
  total_cents: number;
  withdrawable_cents: number;
  non_withdrawable_cents: number;
}

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}


export function PaymentCheckout() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { isLoading: isAuthLoading, user } = useAuth();
  const queryClient = useQueryClient();
  const t = useLabels();
  const [hasTrackedPaymentStart, setHasTrackedPaymentStart] = useState(false);
  const [searchParams] = useSearchParams();
  // Where this checkout was opened from (cart "Pay only this", invoice list, invoice page),
  // stamped on its payment events to measure how families choose between bundle and single.
  const checkoutSource = useMemo(
    () => (invoiceId ? checkoutSourceFor(invoiceId, searchParams) : null),
    [invoiceId, searchParams],
  );
  const [showBankSetupSuccess, setShowBankSetupSuccess] = useState(false);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [bankMenuOpen, setBankMenuOpen] = useState(false);
  const [isWalletPaying, setIsWalletPaying] = useState(false);
  const [isWalletSplitPaying, setIsWalletSplitPaying] = useState(false);
  // Inline fallback-method override at checkout. undefined = use the resolved default;
  // '' = Zelle (pay remainder manually); otherwise a Stripe PaymentMethod id.
  const [pickedFallbackId, setPickedFallbackId] = useState<string | undefined>(undefined);

  // Detect in-flight payments (processing/pending/created) for this invoice.
  // Used to block checkout when an ACH+BHCS Cash split already covers the balance.
  const { data: inflightPayments } = useQuery<Array<{ id: number; amount: number; status: string }>>({
    queryKey: ['invoice-inflight-payments', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('id, amount, status')
        .eq('invoice_id', invoiceId)
        .in('status', ['processing', 'pending', 'created']);
      if (error) throw error;
      return (data ?? []) as Array<{ id: number; amount: number; status: string }>;
    },
    enabled: !isAuthLoading && !!user?.email && !!invoiceId
  });

  // An ACH bundle payment still settling on this invoice has no payments row to find above.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds({ enabled: !isAuthLoading && !!user?.email && !!invoiceId, invoiceId });
  const heldByCart = !!invoiceId && cartHeldInvoiceIds.has(String(invoiceId));

  const { data: invoice, isLoading: isInvoiceLoading, refetch: refetchInvoice } = useQuery<Invoice>({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          total_amount,
          amount_paid,
          due_date,
          period_start,
          period_end,
          status,
          memo_code,
          is_prorated,
          enrollment:enrollments!inner (
            id,
            student:students (
              first_name,
              last_name
            ),
            program:programs (
              name,
              billing_type,
              type,
              tags,
              brand_key
            )
          )
        `)
        .eq('id', invoiceId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Invoice not found');
      
      const enrollment = Array.isArray(data.enrollment) ? data.enrollment[0] : data.enrollment;
      const student = Array.isArray(enrollment.student) ? enrollment.student[0] : enrollment.student;
      const program = Array.isArray(enrollment.program) ? enrollment.program[0] : enrollment.program;
      
      return {
        id: data.id,
        total_amount: data.total_amount,
        amount_paid: data.amount_paid,
        due_date: data.due_date,
        period_start: data.period_start,
        period_end: data.period_end,
        status: data.status,
        memo_code: data.memo_code,
        is_prorated: Boolean((data as { is_prorated?: boolean }).is_prorated),
        enrollment: {
          id: enrollment.id,
          student,
          program
        }
      } as Invoice;
    },
    enabled: !isAuthLoading && !!user?.email
  });

  // The invoice email's "Pay now" button lands here. Open it on the brand that sold the class.
  useLandingBrand(invoice?.enrollment?.program?.brand_key);

  // Coupon UI state
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponInputValue, setCouponInputValue] = useState<string>(''); // For the unified coupon input
  const [couponDropdownOpen, setCouponDropdownOpen] = useState<boolean>(false);
  const [claimedCoupons, setClaimedCoupons] = useState<AvailableCoupon[]>([]); // Coupons validated but not yet applied
  const [appliedCoupon, setAppliedCoupon] = useState<ApplyCouponResponseRow | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState<boolean>(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState<boolean>(false);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [appliedCouponName, setAppliedCouponName] = useState<string | null>(null);

  // Get parent id for coupon lookups
  const { data: parentId } = useQuery<string | null>({
    queryKey: ['parent-id', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .maybeSingle();
      return (data?.id as unknown as string) ?? null;
    },
    enabled: !!user?.email
  });

  // Wallet balance queries (only when flag is enabled)
  const { data: walletAccountId } = useQuery<string | null>({
    queryKey: ['checkout-wallet-account-id', parentId],
    queryFn: async () => {
      if (!parentId) return null;
      const { data, error } = await supabase
        .from('balance_accounts')
        .select('id')
        .eq('parent_id', parentId)
        .maybeSingle();
      if (error) throw error;
      return (data?.id as unknown as string) ?? null;
    },
    enabled: !!parentId,
  });

  const { data: walletBalance } = useQuery<WalletBalanceTotals>({
    queryKey: ['checkout-wallet-balance', walletAccountId],
    queryFn: async () => {
      if (!walletAccountId) return { total_cents: 0, withdrawable_cents: 0, non_withdrawable_cents: 0 };
      const { data, error } = await supabase.rpc('get_balance', { p_account_id: walletAccountId });
      if (error) throw error;
      const row = data?.[0];
      return {
        total_cents: row?.total_cents ?? 0,
        withdrawable_cents: row?.withdrawable_cents ?? 0,
        non_withdrawable_cents: row?.non_withdrawable_cents ?? 0,
      };
    },
    enabled: !!walletAccountId,
  });

  // The parent's currently-effective fallback (saved wallet fallback, else their default
  // payment method) as a structured option. Used as the default selection for the inline
  // fallback picker in the split-pay notice.
  type ResolvedFallback = { id: string; kind: 'card' | 'bank_account'; label: string; brand?: string | null } | null;
  const { data: resolvedFallback } = useQuery<ResolvedFallback>({
    queryKey: ['checkout-resolved-fallback', walletAccountId, parentId],
    queryFn: async () => {
      if (!walletAccountId || !parentId) return null;
      const { data: prefs } = await supabase
        .from('balance_preferences')
        .select('autopay_fallback_payment_method_id')
        .eq('account_id', walletAccountId)
        .maybeSingle();
      const pmId = prefs?.autopay_fallback_payment_method_id ?? null;

      // No explicit wallet fallback — mirror edge function logic and resolve from the
      // parent's default payment method so split-pay works without wallet config.
      if (!pmId) {
        const { data: parentPrefs } = await supabase
          .from('parent_payment_preferences')
          .select('default_payment_method, default_bank_account_setup_intent_id')
          .eq('parent_id', parentId)
          .maybeSingle();
        const defaultMethod = (parentPrefs as { default_payment_method?: string | null; default_bank_account_setup_intent_id?: string | null } | null)?.default_payment_method;
        const defaultBankIntentId = (parentPrefs as { default_payment_method?: string | null; default_bank_account_setup_intent_id?: string | null } | null)?.default_bank_account_setup_intent_id;

        if (defaultMethod === 'bank_account' && defaultBankIntentId) {
          const { data: bank } = await supabase
            .from('bank_account_setup_intents')
            .select('payment_method_id, bank_name, last4')
            .eq('id', defaultBankIntentId)
            .eq('parent_id', parentId)
            .maybeSingle();
          const bankPmId = (bank as { payment_method_id?: string | null } | null)?.payment_method_id;
          if (bankPmId) {
            const bankName = (bank as { bank_name: string | null }).bank_name ?? 'Bank';
            const last4 = (bank as { last4: string | null }).last4 ?? '••••';
            return { id: bankPmId, kind: 'bank_account', label: `${bankName} •••• ${last4} (ACH)` };
          }
        } else if (defaultMethod === 'card') {
          const { data: card } = await supabase
            .from('card_payment_methods')
            .select('stripe_payment_method_id, brand, last4')
            .eq('parent_id', parentId)
            .eq('is_active', true)
            .maybeSingle();
          const cardPmId = (card as { stripe_payment_method_id?: string | null } | null)?.stripe_payment_method_id;
          if (cardPmId) {
            const brand = (card as { brand: string | null }).brand;
            const last4 = (card as { last4: string | null }).last4;
            return { id: cardPmId, kind: 'card', label: savedCardLabel(brand, last4), brand };
          }
        }
        return null;
      }

      const { data: card } = await supabase
        .from('card_payment_methods')
        .select('brand, last4')
        .eq('stripe_payment_method_id', pmId)
        .eq('parent_id', parentId)
        .maybeSingle();
      if (card) {
        const brand = (card as { brand: string | null }).brand;
        const last4 = (card as { last4: string | null }).last4;
        return { id: pmId, kind: 'card', label: savedCardLabel(brand, last4), brand };
      }
      const { data: bank } = await supabase
        .from('bank_account_setup_intents')
        .select('bank_name, last4')
        .eq('payment_method_id', pmId)
        .eq('parent_id', parentId)
        .maybeSingle();
      if (bank) {
        const bankName = (bank as { bank_name: string | null }).bank_name ?? 'Bank';
        const last4 = (bank as { last4: string | null }).last4 ?? '••••';
        return { id: pmId, kind: 'bank_account', label: `${bankName} •••• ${last4} (ACH)` };
      }
      return null;
    },
    enabled: !!walletAccountId && !!parentId,
  });

  // All of the parent's saved cards + connected banks — the options for the inline
  // fallback picker shown in the split-pay notice.
  const { data: fallbackOptions } = useQuery<FallbackOption[]>({
    queryKey: ['checkout-fallback-options', parentId],
    queryFn: async () => {
      if (!parentId) return [];
      const [{ data: bankRows, error: bankErr }, { data: cardRows, error: cardErr }] = await Promise.all([
        supabase
          .from('bank_account_setup_intents')
          .select('id, bank_name, last4, status, is_active, payment_method_id')
          .eq('parent_id', parentId)
          .eq('is_active', true)
          .eq('status', 'succeeded')
          .not('payment_method_id', 'is', null),
        supabase
          .from('card_payment_methods')
          .select('id, brand, last4, is_active, stripe_payment_method_id')
          .eq('parent_id', parentId)
          .eq('is_active', true),
      ]);
      if (bankErr) throw bankErr;
      if (cardErr) throw cardErr;

      const bankOptions: FallbackOption[] = (bankRows ?? []).flatMap((r) => {
        const pmId = (r as { payment_method_id: string | null }).payment_method_id;
        if (!pmId) return [];
        const bankName = (r as { bank_name: string | null }).bank_name ?? 'Bank';
        const last4 = (r as { last4: string | null }).last4 ?? '••••';
        return [{ id: pmId, kind: 'bank_account', label: `${bankName} •••• ${last4} (ACH)`, sourceId: (r as { id: string }).id }];
      });
      const cardOptions: FallbackOption[] = (cardRows ?? []).flatMap((r) => {
        const stripeId = (r as { stripe_payment_method_id: string | null }).stripe_payment_method_id;
        if (!stripeId) return [];
        const brand = (r as { brand: string | null }).brand;
        const last4 = (r as { last4: string | null }).last4;
        return [{ id: stripeId, kind: 'card', label: savedCardLabel(brand, last4), sourceId: stripeId, brand }];
      });
      return [...bankOptions, ...cardOptions];
    },
    enabled: !!parentId,
  });

  // Normalize parent id in case a wrapped object sneaks through from a cache or mock
  const parentIdStr = useMemo(() => {
    if (!parentId) return null;
    if (typeof parentId === 'string') return parentId;
    // Fallback: some mocks may return { id: '...' }
    // @ts-expect-error safeguard for runtime shapes
    if (typeof parentId === 'object' && parentId?.id) return String(parentId.id);
    return String(parentId);
  }, [parentId]);

  // Available coupons for this parent and invoice
  const { data: availableCoupons = [] } = useQuery<AvailableCoupon[]>({
    queryKey: ['available-coupons', parentIdStr, invoiceId],
    queryFn: async () => {
      if (!parentIdStr || !invoiceId) return [];
      const { data, error } = await supabase
        .from('coupons')
        .select(`
          id,
          code,
          type,
          value,
          usage_limit,
          used_count,
          expires_at,
          applicable_invoice_id,
          template:coupon_templates(name, description, active)
        `)
        .or(`user_id.eq.${encodeURIComponent(parentIdStr)},applicable_invoice_id.eq.${encodeURIComponent(String(invoiceId))}`)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const nowIso = new Date().toISOString();
      return (data as unknown as AvailableCoupon[]).filter((c) => {
        const notExpired = !c.expires_at || c.expires_at >= nowIso;
        const withinUsage = c.usage_limit == null || c.used_count < c.usage_limit;
        const templateActive = c.template?.active !== false;
        return notExpired && withinUsage && templateActive;
      });
    },
    enabled: !!parentId && !!invoiceId && !isInvoiceLoading
  });

  // Detect any coupon already applied to this invoice (persists across refresh)
  interface AppliedCouponDetectResult { row: ApplyCouponResponseRow | null; templateName: string | null }
  const { data: detectedCoupon } = useQuery<AppliedCouponDetectResult>({
    queryKey: ['applied-coupon', parentIdStr, invoiceId],
    queryFn: async () => {
      if (!parentIdStr || !invoiceId) return { row: null, templateName: null };
      // Find coupon tied to this invoice
      const { data: couponRows, error } = await supabase
        .from('coupons')
        .select(`
          id,
          code,
          type,
          value,
          usage_limit,
          used_count,
          expires_at,
          template:coupon_templates(name)
        `)
        .eq('applicable_invoice_id', invoiceId)
        .limit(1);
      if (error) throw error;
      if (!couponRows || couponRows.length === 0) return { row: null, templateName: null };

      const coupon = couponRows[0] as unknown as AppliedCouponServerRow;
      // Query the discount line items for this coupon code to compute the absolute discount
      const { data: items, error: itemsError } = await supabase
        .from('invoicing_line_items')
        .select('amount, description')
        .eq('invoice_id', invoiceId)
        .ilike('description', `%[${coupon.code}]%`);
      if (itemsError) throw itemsError;
      const discountAbs = Math.abs((items || []).reduce((sum: number, li: { amount: number }) => sum + (Number(li.amount) || 0), 0));

      const row: ApplyCouponResponseRow = {
        success: true,
        error: null,
        discount_amount: discountAbs,
        coupon_id: coupon.id,
        code: coupon.code,
        coupon_type: coupon.type,
        coupon_value: coupon.value,
        used_count: coupon.used_count,
        usage_limit: coupon.usage_limit,
        expires_at: coupon.expires_at,
      };
      return { row, templateName: coupon.template?.name || null };
    },
    enabled: !!parentIdStr && !!invoiceId && !isInvoiceLoading,
  });

  useEffect(() => {
    if (detectedCoupon?.row) {
      setAppliedCoupon(detectedCoupon.row);
      const matched = availableCoupons.find((c) => c.code === detectedCoupon.row?.code);
      setAppliedCouponName(matched?.template?.name || detectedCoupon.templateName || null);
    }
  }, [detectedCoupon, availableCoupons]);

  // Query to check if bank account is connected (scoped to current parent)
  const { data: bankAccountSetup, isLoading: isBankAccountLoading } = useQuery({
    queryKey: ['bank-account-setup', parentIdStr],
    queryFn: async () => {
      if (!parentIdStr) return null;
      const { data, error } = await supabase
        .from('bank_account_setup_intents')
        .select('*')
        .eq('parent_id', parentIdStr)
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!parentId
  });

  // Start with card as default, but update based on bank account status
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'zelle' | 'bank_account' | 'wallet'>('card');
  // Brand of the saved card PaymentForm will charge (null = a newly entered card). It prices
  // the processing fee: a saved `link` card is a bank payment at 2.6% instead of 2.9%.
  const [chargedCardBrand, setChargedCardBrand] = useState<string | null>(null);

  // Update payment method when bank account data becomes available
  useEffect(() => {
    const bankSetupComplete = searchParams.get('bank_setup_complete');
    // Only set to bank_account if we have confirmed bank setup OR if bank account is connected and query has completed
    if (bankSetupComplete === 'true' || (!isBankAccountLoading && bankAccountSetup)) {
      setSelectedPaymentMethod('bank_account');
    }
  }, [searchParams, bankAccountSetup, isBankAccountLoading]);

  // Respect saved default payment method if available
  const { data: prefs } = useQuery({
    queryKey: ['parent-payment-prefs', parentIdStr],
    queryFn: async () => {
      if (!parentIdStr) return null;
      const { data, error } = await supabase
        .from('parent_payment_preferences')
        .select('default_payment_method')
        .eq('parent_id', parentIdStr)
        .maybeSingle();
      if (error) throw error;
      return data as { default_payment_method: 'card' | 'bank_account' | 'zelle' } | null;
    },
    enabled: !!parentId
  });

  useEffect(() => {
    if (!prefs?.default_payment_method) return;
    if (prefs.default_payment_method === 'bank_account') {
      if (!isBankAccountLoading && bankAccountSetup) setSelectedPaymentMethod('bank_account');
    } else {
      setSelectedPaymentMethod(prefs.default_payment_method);
    }
  }, [prefs, bankAccountSetup, isBankAccountLoading]);

  // Track payment start when invoice is loaded and payment form is displayed (only once)
  useEffect(() => {
    if (invoice && user && invoiceId && !hasTrackedPaymentStart) {
      const amountDue = invoice.total_amount - (invoice.amount_paid || 0);
      
      // Build metadata with payment method and payment intent if ACH
      const metadata: Record<string, unknown> = {
        student_name: `${invoice.enrollment.student.first_name} ${invoice.enrollment.student.last_name}`,
        program_name: invoice.enrollment.program.name,
        invoice_status: invoice.status,
        memo_code: invoice.memo_code,
        payment_method: selectedPaymentMethod,
        ...(checkoutSource ? { source: checkoutSource } : {}),
      };

      // If it's ACH and we have a bank account setup, include payment intent info
      if (selectedPaymentMethod === 'bank_account' && bankAccountSetup) {
        metadata.payment_intent = bankAccountSetup.setup_intent_id;
        metadata.bank_account_setup_intent_id = bankAccountSetup.id;
      }

      analytics.trackPaymentStart({
        user,
        invoiceId,
        amount: amountDue,
        sessionId: getSessionId(),
        metadata
      });
      setHasTrackedPaymentStart(true);
    }
  }, [invoice, user, invoiceId, hasTrackedPaymentStart, selectedPaymentMethod, bankAccountSetup, checkoutSource]);

  // Redirect to invoice details if invoice is already paid OR if an in-flight payment
  // (e.g. ACH still processing after an ACH+BHCS Cash split) already covers the balance.
  // Without this guard a parent could accidentally overpay while ACH is settling.
  useEffect(() => {
    if (!invoice) return;

    const alreadyPaid = invoice.status === 'paid' || invoice.amount_paid >= invoice.total_amount;

    const remaining = invoice.total_amount - (invoice.amount_paid ?? 0);
    const inflightTotal = (Array.isArray(inflightPayments) ? inflightPayments : []).reduce((sum, p) => sum + p.amount, 0);
    // Allow a $0.01 rounding tolerance
    const coveredByInflight = inflightTotal >= remaining - 0.01 && remaining > 0.01;

    if (alreadyPaid || coveredByInflight || heldByCart) {
      const timer = setTimeout(() => {
        navigate(`/invoices/${invoiceId}`, {
          replace: true,
          state: { paymentComplete: true }
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [invoice, inflightPayments, heldByCart, invoiceId, navigate]);

  // Handle bank account setup completion when returning from Stripe
  useEffect(() => {
    const bankSetupComplete = searchParams.get('bank_setup_complete');
    const setupIntentId = searchParams.get('setup_intent_id');
    
    if (bankSetupComplete === 'true' && setupIntentId) {
      // Update the setup intent status
      const updateSetupIntent = async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData.session?.access_token;

          if (!token) {
            setError('Authentication required. Please log in and try again.');
            return;
          }

          const setupResponse = await fetch(ENDPOINTS.updateBankAccountSetupIntent, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ setupIntentId }),
          });

          if (setupResponse.ok) {
            const data = await setupResponse.json();
            if (data.status === 'succeeded') {
              // Track bank account addition/update event
              if (user) {
                trackEvent({
                  eventType: 'add_payment_method',
                  user: user,
                  entityType: 'payment_method',
                  sessionId: getSessionId(),
                  metadata: {
                    payment_method: 'ach',
                    setup_intent_id: setupIntentId,
                    payment_intent: setupIntentId,
                    bank_details: data.bankDetails || null,
                    invoice_id: invoiceId
                  },
                  email: user.email,
                });
              }

              setShowBankSetupSuccess(true);
              queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
              
              // Remove the query parameters from URL
              navigate(`/payment-checkout/${invoiceId}`, { replace: true });
              
              // Hide success message after 5 seconds
              setTimeout(() => setShowBankSetupSuccess(false), 5000);
            }
          }
        } catch (error) {
          console.error('Error updating bank account setup:', error);
        }
      };

      updateSetupIntent();
    }
  }, [searchParams, invoiceId, navigate, queryClient]);

  const handleWalletPay = async () => {
    if (!invoiceId || !parentIdStr) return;
    setWalletError(null);
    setIsWalletPaying(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setWalletError('Authentication required. Please log in and try again.');
        return;
      }
      const response = await fetch(ENDPOINTS.balanceApplyToInvoice, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_id: String(invoiceId), parent_id: parentIdStr, allow_split: false }),
      });
      const result = await response.json() as { status?: string; error?: string; message?: string };
      if (!response.ok) {
        if (result.error === 'INSUFFICIENT_BALANCE') {
          setWalletError('Your BHCS Cash balance does not cover the full invoice. Please choose a different payment method.');
        } else {
          setWalletError(result.message ?? 'Failed to apply wallet balance. Please try again.');
        }
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices-list'] }),
        queryClient.invalidateQueries({ queryKey: ['checkout-wallet-balance'] }),
      ]);
      navigate(`/invoices/${invoiceId}`, { replace: true });
    } catch (err) {
      console.error('Wallet pay error:', err);
      setWalletError('Failed to process wallet payment. Please try again.');
    } finally {
      setIsWalletPaying(false);
    }
  };

  const handleSplitPay = async () => {
    if (!invoiceId || !parentIdStr) return;
    setWalletError(null);
    setIsWalletSplitPaying(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setWalletError('Authentication required. Please log in and try again.');
        return;
      }
      // If the parent switched the fallback method inline, persist it first so the edge
      // function (which resolves the fallback from balance_preferences) charges that method.
      const effectiveFallbackId = pickedFallbackId !== undefined ? pickedFallbackId : (resolvedFallback?.id ?? '');
      if (walletAccountId && pickedFallbackId !== undefined && effectiveFallbackId !== (resolvedFallback?.id ?? '')) {
        await supabase
          .from('balance_preferences')
          .upsert(
            { account_id: walletAccountId, autopay_fallback_payment_method_id: effectiveFallbackId || null },
            { onConflict: 'account_id' }
          );
      }
      const response = await fetch(ENDPOINTS.balanceApplyToInvoice, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_id: String(invoiceId), parent_id: parentIdStr, allow_split: true }),
      });
      const result = await response.json() as { status?: string; error?: string; message?: string };
      if (!response.ok) {
        if (result.error === 'NO_FALLBACK_PAYMENT_METHOD') {
          setWalletError('No card or bank account on file. Please add a payment method to your account before using split pay.');
        } else {
          setWalletError(result.message ?? 'Failed to process split payment. Please try again.');
        }
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices-list'] }),
        queryClient.invalidateQueries({ queryKey: ['checkout-wallet-balance'] }),
      ]);
      navigate(`/invoices/${invoiceId}`, { replace: true });
    } catch (err) {
      console.error('Split pay error:', err);
      setWalletError('Failed to process split payment. Please try again.');
    } finally {
      setIsWalletSplitPaying(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string, paymentIntent: ExtendedPaymentIntent) => {
    try {
      console.log('Payment success handler called with payment intent ID:', paymentIntentId);
      console.log('Payment intent object:', paymentIntent);
      
      if (!invoice) return;
      
      const amountDue = invoice.total_amount - (invoice.amount_paid || 0);
      // Only add processing fee for card payments
      const stripeFee = selectedPaymentMethod === 'card' ? calculateProcessingFee(amountDue, chargedCardBrand) : 0;
      const totalAmount = amountDue + stripeFee;
      
      // Get parent ID for paid_by
      const { data: parent } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user?.email)
        .single();
      if (!parent) throw new Error('Parent not found');
      
      // Only add processing fee line item for card payments
      if (selectedPaymentMethod === 'card') {
        // Get the Stripe fee line item config - should already exist in the database
        const { data: feeConfig, error: configError } = await supabase
          .from('line_item_configs')
          .select('id')
          .eq('name', 'Stripe Processing Fee')
          .single();
          
        if (configError) {
          console.error('Error fetching Stripe fee line item config:', configError);
          throw configError;
        }
        
        // Add Stripe fee as a line item to the invoice using the pre-configured line item config
        const { error: lineItemError } = await supabase
          .from('invoicing_line_items')
          .insert({
            invoice_id: invoice.id,
            description: `Stripe Processing Fee (${processingFeeLabel(chargedCardBrand)})`,
            amount: stripeFee,
            line_item_config_id: feeConfig.id
          });

        if (lineItemError) {
          console.error('Error adding fee line item:', lineItemError);
          throw lineItemError;
        }
        
        // Try to update the invoice total to include the fee
        try {
          // First try using the RPC function which should bypass policy recursion
          const { error: rpcError } = await supabase.rpc('update_invoice_total', {
            p_invoice_id: invoice.id,
            p_new_total: invoice.total_amount + stripeFee
          });
          
          if (!rpcError) {
            console.log('Successfully updated invoice total via RPC');
          } else {
            console.warn('RPC function failed, likely not deployed yet:', rpcError.message);
            
            // If RPC fails, we don't try direct updates
            // Just continue with the payment - admin can fix the invoice total later
          }
        } catch (err) {
          console.error('Failed to update invoice total:', err);
          // If anything fails, we'll just continue - the line item is added
          // but the invoice total may need admin adjustment
        }
      }
      
      // Get bank account setup intent ID if using bank account payment
      let bankAccountSetupIntentId = null;
      if (selectedPaymentMethod === 'bank_account') {
        const { data: setupIntent } = await supabase
          .from('bank_account_setup_intents')
          .select('id')
          .eq('parent_id', parent.id)
          .eq('is_active', true)
          .eq('status', 'succeeded')
          .order('created_at', { ascending: false })
          .maybeSingle();
        
        bankAccountSetupIntentId = setupIntent?.id || null;
      }

      // Create or update payment record (handles race condition with webhook)
      // Map UI payment method to database payment method
      const dbPaymentMethod = selectedPaymentMethod === 'bank_account' ? 'ach' : selectedPaymentMethod;
      const stripePaymentId = selectedPaymentMethod === 'card' || selectedPaymentMethod === 'bank_account' ? paymentIntentId : null;
      
      let payment;
      
      if (stripePaymentId) {
        // For Stripe payments, first check if webhook already created the payment record
        const { data: existingPayment } = await supabase
          .from('payments')
          .select('*')
          .eq('stripe_payment_id', stripePaymentId)
          .maybeSingle();
          
        if (existingPayment) {
          console.log('Payment record already exists (created by webhook), using existing record');
          payment = existingPayment;
        } else {
          // Create new payment record with status based on actual payment intent status
          let initialStatus = 'pending';
          if (paymentIntent.status === 'succeeded') {
            initialStatus = 'completed';
            console.log('Payment intent already succeeded, setting initial status to completed');
          } else if (paymentIntent.status === 'processing') {
            initialStatus = 'processing';
            console.log('Payment intent is processing, setting initial status to processing');
          }
          
          const { data: newPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              invoice_id: invoiceId,
              amount: totalAmount,
              payment_method: dbPaymentMethod,
              payment_date: new Date().toISOString(),
              status: initialStatus, // Use actual payment intent status
              stripe_payment_id: stripePaymentId,
              bank_account_setup_intent_id: bankAccountSetupIntentId,
              paid_by: parent.id
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Error creating payment record:', paymentError);
            throw paymentError;
          }
          payment = newPayment;
        }
      } else {
        // For non-Stripe payments (e.g., Zelle), always create new record
        const { data: newPayment, error: paymentError } = await supabase
          .from('payments')
          .insert({
            invoice_id: invoiceId,
            amount: totalAmount,
            payment_method: dbPaymentMethod,
            payment_date: new Date().toISOString(),
            status: 'pending',
            stripe_payment_id: null,
            bank_account_setup_intent_id: bankAccountSetupIntentId,
            paid_by: parent.id
          })
          .select()
          .single();

        if (paymentError) {
          console.error('Error creating payment record:', paymentError);
          throw paymentError;
        }
        payment = newPayment;
      }
      
      // Only add transaction records for card and bank account payments
      if (selectedPaymentMethod === 'card' || selectedPaymentMethod === 'bank_account') {
        // Check if a transaction record already exists
        const { count, error: countError } = await supabase
          .from('stripe_transactions')
          .select('*', { count: 'exact', head: true })
          .eq('payment_intent_id', paymentIntentId);
          
        if (countError) {
          console.error('Error checking for existing transaction:', countError);
        }
        
        console.log(`Found ${count || 0} existing transaction records for this payment intent`);
        
        // Prepare transaction details
        const transactionDetails = {
          payment_id: payment.id,
          status: paymentIntent.status,
          amount: paymentIntent.amount / 100, // Convert from cents to dollars
          currency: paymentIntent.currency,
          payment_method_type: paymentIntent.payment_method_types?.[0],
          payment_method_details: {
            card: paymentIntent.payment_method_details?.card,
            payment_success_date: new Date().toISOString()
          },
          receipt_url: paymentIntent.charges?.data?.[0]?.receipt_url
        };
        
        let transactionError;
        
        // If record exists, update it
        if (count && count > 0) {
          console.log('Updating existing transaction record');
          const { error } = await supabase
            .from('stripe_transactions')
            .update(transactionDetails)
            .eq('payment_intent_id', paymentIntentId);
            
          transactionError = error;
          if (error) {
            console.error('Error updating transaction record:', error);
          } else {
            console.log('Transaction record updated successfully');
          }
        } 
        // Otherwise insert a new record
        else {
          console.log('Creating new transaction record');
          const { error } = await supabase
            .from('stripe_transactions')
            .insert({
              ...transactionDetails,
              payment_intent_id: paymentIntentId,
              client_secret: paymentIntent.client_secret
            });
            
          transactionError = error;
          if (error) {
            console.error('Error creating transaction record:', error);
          } else {
            console.log('Transaction record created successfully');
          }
        }
        
        // If there was an error, throw it after we've tried both approaches
        if (transactionError) throw transactionError;
      }

      // Track payment completion
      analytics.trackPaymentComplete({
        user,
        paymentId: payment.id,
        invoiceId: invoiceId,
        amount: totalAmount,
        paymentMethod: selectedPaymentMethod,
        sessionId: getSessionId(),
        metadata: {
          stripe_payment_id: selectedPaymentMethod === 'card' ? paymentIntentId : null,
          student_name: `${invoice.enrollment.student.first_name} ${invoice.enrollment.student.last_name}`,
          program_name: invoice.enrollment.program.name,
          invoice_status: invoice.status,
          memo_code: invoice.memo_code,
          stripe_fee: selectedPaymentMethod === 'card' ? stripeFee : 0,
          ...(checkoutSource ? { source: checkoutSource } : {}),
        }
      });

      // Invalidate relevant queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices-list'] }),
        queryClient.invalidateQueries({ queryKey: ['payments-list'] })
      ]);

      // Navigate to invoice details page with replace (instead of push)
      // This removes the payment page from history so users can't go back to it
      navigate(`/invoices/${invoiceId}`, { replace: true });
    } catch (err) {
      console.error('Error recording payment:', err);
      setError('Failed to record payment. Please contact support.');
    }
  };

  if (isAuthLoading || isInvoiceLoading) {
    return (
      <div className="p-4">
        <div data-testid="loading-skeleton" className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="bg-white p-6 rounded-lg shadow space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Not Found: </strong>
          <span className="block sm:inline">Invoice not found</span>
        </div>
      </div>
    );
  }

  // Check if invoice is voided and prevent payment
  if (invoice.status.toLowerCase() === 'voided') {
    return (
      <div className="p-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Payment Checkout</h1>
          
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    Invoice #{invoice.id}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Student: {invoice.enrollment.student.first_name} {invoice.enrollment.student.last_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    Program: {invoice.enrollment.program.name}
                  </p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    VOIDED
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      This invoice has been voided and cannot be paid. Please contact support if you have any questions.
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/invoices')}
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Return to Invoices
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use the invoice total from the database directly.
  // The apply/remove coupon RPC updates the invoice total server-side,
  // so we avoid double-subtracting any discount in the UI.
  const baseAmountDue = Math.max(0, invoice.total_amount - (invoice.amount_paid || 0));
  const amountDue = baseAmountDue;
  const invoiceAmountDisplay = appliedCoupon ? baseAmountDue + appliedCoupon.discount_amount : baseAmountDue;
  // For wallet split-pay with a card fallback: compute the fee on the card remainder
  // so the top summary shows invoice + fee = total, same as full-card checkout.
  const walletAmountDueCents = Math.round(amountDue * 100);
  const walletBonusApplied = Math.min(walletBalance?.non_withdrawable_cents ?? 0, walletAmountDueCents);
  const walletWithdrawableApplied = Math.min(walletBalance?.withdrawable_cents ?? 0, walletAmountDueCents - walletBonusApplied);
  const walletRemainderCents = walletAmountDueCents - walletBonusApplied - walletWithdrawableApplied;
  // Effective fallback for the split remainder: the inline picker's choice, else the resolved
  // default. '' = Zelle (manual). Everything below (label, fee, breakdown, notice) derives
  // from this so switching the picker updates the UI live.
  const effectiveFallbackId = pickedFallbackId !== undefined ? pickedFallbackId : (resolvedFallback?.id ?? '');
  const fallbackOptionsList: FallbackOption[] = Array.isArray(fallbackOptions) ? fallbackOptions : [];
  const selectedFallbackOption: FallbackOption | null = effectiveFallbackId
    ? (fallbackOptionsList.find((o) => o.id === effectiveFallbackId)
        ?? (resolvedFallback && resolvedFallback.id === effectiveFallbackId
          ? { id: resolvedFallback.id, kind: resolvedFallback.kind, label: resolvedFallback.label, brand: resolvedFallback.brand ?? null }
          : null))
    : null;
  const fallbackPmLabel: string | null = selectedFallbackOption?.label ?? null;
  const isFallbackCardPm = selectedFallbackOption?.kind === 'card';
  const walletSplitCardFeeCents = (selectedPaymentMethod === 'wallet' && walletRemainderCents > 0 && isFallbackCardPm)
    ? Math.round(calculateProcessingFee(walletRemainderCents / 100, selectedFallbackOption?.brand) * 100)
    : 0;
  const stripeFee = selectedPaymentMethod === 'card' ? calculateProcessingFee(amountDue, chargedCardBrand) : walletSplitCardFeeCents / 100;
  const stripeFeeLabel = processingFeeLabel(selectedPaymentMethod === 'card' ? chargedCardBrand : selectedFallbackOption?.brand);
  const totalWithFee = amountDue + stripeFee;
  const isCampInvoice = isSummerCampProgram(invoice.enrollment.program);
  const isCampInNoRefundWindow = isCampInvoice && isWithinCampNoRefundWindow(invoice.period_start);

  return (
    <div className="p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Payment Checkout</h1>
        
        {/* Bank Setup Success Message */}
        {showBankSetupSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Bank account successfully connected!
                </p>
                <p className="text-sm text-green-600">
                  You can now use your bank account for secure, low-cost payments.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow overflow-visible">
          <div className="p-6 border-b">
            <div className="flex items-start justify-between">
              <div>
                <button
                  onClick={() => { trackEvent({ eventType: 'payment_abandon', user, entityType: 'invoice', entityId: String(invoiceId), metadata: { student_name: `${invoice.enrollment.student.first_name} ${invoice.enrollment.student.last_name}`, program_name: invoice.enrollment.program.name, amount: invoice.total_amount - invoice.amount_paid, ...(checkoutSource ? { source: checkoutSource } : {}) } }); navigate(`/invoices/${invoiceId}`); }}
                  className="flex items-center space-x-1 text-sm font-medium text-blue-600 hover:underline mb-3"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Back to Invoice</span>
                </button>
                <p className="text-sm text-gray-600">
                  Student: {invoice.enrollment.student.first_name} {invoice.enrollment.student.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  Program: {invoice.enrollment.program.name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">{t('label.amountDue')}</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  ${amountDue.toFixed(2)}
                </p>
                {(() => {
                  return invoice.is_prorated ? (
                    <p className="text-xs text-yellow-700 mt-1 font-medium">
                      ℹ️ Prorated billing - view invoice details for full breakdown
                    </p>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          <div className="p-6">
            {isCampInvoice && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="camp-policy-payment-checkout">
                <p className="text-sm font-semibold text-amber-900">Summer camp cancellation and refund policy</p>
                <ul className="mt-2 list-disc pl-5 text-sm text-amber-800 space-y-1">
                  {SUMMER_CAMP_POLICY.cancellationTiers.map((tier) => (
                    <li key={tier}>{tier}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-700">
                  Registration is complete only after payment is received. Contact {SUMMER_CAMP_POLICY.supportEmail} for policy-related requests.
                </p>
                {isCampInNoRefundWindow && (
                  <p className="mt-2 text-xs font-semibold text-red-700">
                    This camp period is within 2 weeks of start; no refunds, credits, or switches are available online.
                  </p>
                )}
              </div>
            )}
            {/* Payment Method Selection */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => { setSelectedPaymentMethod('card'); setWalletError(null); }}
                  className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl border text-left transition-colors ${
                    selectedPaymentMethod === 'card'
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <PaymentMethodIcon type="card" size={22} />
                  <span className="text-sm font-medium text-gray-900">Credit/Debit Card</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedPaymentMethod('bank_account'); setWalletError(null); }}
                  className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl border text-left transition-colors ${
                    selectedPaymentMethod === 'bank_account'
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <PaymentMethodIcon type="bank_account" size={22} />
                  <span className="text-sm font-medium text-gray-900">Bank Account (ACH)</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedPaymentMethod('zelle'); setWalletError(null); }}
                  className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl border text-left transition-colors ${
                    selectedPaymentMethod === 'zelle'
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <PaymentMethodIcon type="zelle" size={22} />
                  <span className="text-sm font-medium text-gray-900">Pay via Zelle®</span>
                </button>
                {(walletBalance?.total_cents ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => { setSelectedPaymentMethod('wallet'); setWalletError(null); }}
                    className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl border text-left transition-colors ${
                      selectedPaymentMethod === 'wallet'
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="w-[35px] h-[22px] rounded-md bg-emerald-500 flex items-center justify-center shrink-0">
                      <CurrencyDollarIcon className="w-4 h-4 text-white" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900">BHCS Cash</span>
                      <span className="block text-xs text-gray-500">{formatUsdFromCents(walletBalance!.total_cents)} available</span>
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mb-6 bg-gray-50 p-4 rounded">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Invoice Amount:</span>
                <span className="font-medium">${invoiceAmountDisplay.toFixed(2)}</span>
              </div>
              {appliedCoupon && appliedCoupon.discount_amount != null && (
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-600">Coupon ({appliedCoupon.code}):</span>
                  <span className="font-medium">- ${appliedCoupon.discount_amount.toFixed(2)}</span>
                </div>
              )}
              
              {stripeFee > 0 && (
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-600">Processing Fee ({stripeFeeLabel}):</span>
                  <span className="font-medium">${stripeFee.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                <span className="text-sm font-semibold">Total Amount:</span>
                <span className="font-bold">${totalWithFee.toFixed(2)}</span>
              </div>
            </div>

            {/* Unified Coupon Dropdown - single dropdown with input inside */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Have a coupon?</label>
              {!appliedCoupon && (
                <div className="flex gap-2 items-start w-full">
                  {/* Custom dropdown with input inside */}
                  <div className="flex-1 relative">
                    <button
                      type="button"
                      onClick={() => setCouponDropdownOpen(!couponDropdownOpen)}
                      className="w-full border rounded px-3 py-2 text-left bg-white flex items-center justify-between text-xs sm:text-sm"
                      disabled={isApplyingCoupon}
                    >
                      <span className={`truncate min-w-0 ${couponCode ? 'text-gray-900' : 'text-gray-500'}`}>
                        {couponCode
                          ? (() => {
                              const allCoupons = [...(Array.isArray(availableCoupons) ? availableCoupons : []), ...claimedCoupons];
                              const selected = allCoupons.find(c => c.code === couponCode);
                              return selected ? `${selected.template?.name || selected.code} — ${selected.type === 'percentage' ? `${selected.value}%` : `$${selected.value.toFixed(2)}`} off` : couponCode;
                            })()
                          : 'Select or enter a coupon code'
                        }
                      </span>
                      <svg className={`w-4 h-4 shrink-0 ml-2 text-gray-400 transition-transform ${couponDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Dropdown panel */}
                    {couponDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg">
                        {/* Input field inside dropdown */}
                        <div className="p-2 border-b">
                          <div className="relative">
                            <input
                              type="text"
                              value={couponInputValue}
                              onChange={(e) => {
                                setCouponInputValue(e.target.value.toUpperCase());
                                setCouponMessage(null);
                              }}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter' && couponInputValue.trim()) {
                                  e.preventDefault();
                                  const codeToValidate = couponInputValue.trim();
                                  
                                  // Check if already in available or claimed list
                                  const allCoupons = [...(Array.isArray(availableCoupons) ? availableCoupons : []), ...claimedCoupons];
                                  const existing = allCoupons.find(c => c.code === codeToValidate);
                                  if (existing) {
                                    setCouponCode(codeToValidate);
                                    setCouponInputValue('');
                                    setCouponDropdownOpen(false);
                                    setCouponMessage(null);
                                    return;
                                  }
                                  
                                  // Validate the coupon code
                                  setIsValidatingCoupon(true);
                                  setCouponMessage(null);
                                  try {
                                    const { data, error } = await supabase
                                      .from('coupons')
                                      .select(`
                                        id, code, type, value, usage_limit, used_count, expires_at,
                                        applicable_invoice_id, user_id,
                                        template:coupon_templates(name, description, active)
                                      `)
                                      .eq('code', codeToValidate)
                                      .maybeSingle();
                                    
                                    if (error) throw error;
                                    
                                    if (!data) {
                                      setCouponMessage('Coupon not found');
                                      return;
                                    }
                                    
                                    const coupon = data as unknown as AvailableCoupon & { user_id: string | null };
                                    if (coupon.template?.active === false) {
                                      setCouponMessage('Coupon is not active');
                                      return;
                                    }
                                    if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
                                      setCouponMessage('Coupon has expired');
                                      return;
                                    }
                                    if (coupon.usage_limit != null && coupon.used_count >= coupon.usage_limit) {
                                      setCouponMessage('Coupon usage limit reached');
                                      return;
                                    }
                                    if (coupon.user_id && coupon.user_id !== parentIdStr) {
                                      setCouponMessage('This coupon belongs to another user');
                                      return;
                                    }
                                    if (coupon.applicable_invoice_id && coupon.applicable_invoice_id !== invoiceId) {
                                      setCouponMessage('Coupon is tied to a different invoice');
                                      return;
                                    }
                                    
                                    // Valid! Add to claimed coupons list and select it
                                    const newCoupon: AvailableCoupon = {
                                      id: coupon.id,
                                      code: coupon.code,
                                      type: coupon.type,
                                      value: coupon.value,
                                      usage_limit: coupon.usage_limit,
                                      used_count: coupon.used_count,
                                      expires_at: coupon.expires_at,
                                      applicable_invoice_id: coupon.applicable_invoice_id,
                                      template: coupon.template,
                                    };
                                    
                                    setClaimedCoupons(prev => [...prev.filter(c => c.code !== codeToValidate), newCoupon]);
                                    setCouponCode(codeToValidate);
                                    setCouponInputValue('');
                                    setCouponMessage(null);
                                  } catch (err) {
                                    setCouponMessage('Failed to validate coupon');
                                  } finally {
                                    setIsValidatingCoupon(false);
                                  }
                                }
                              }}
                              className="w-full border rounded px-3 py-2 pr-8 text-sm placeholder:text-[10px] sm:placeholder:text-sm"
                              placeholder="Enter coupon code and press Enter"
                              autoFocus
                              disabled={isValidatingCoupon}
                            />
                            {isValidatingCoupon && (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              </div>
                            )}
                          </div>
                          {couponMessage && (
                            <p className={`text-xs mt-1 ${
                              couponMessage.toLowerCase().includes('fail') || 
                              couponMessage.toLowerCase().includes('invalid') || 
                              couponMessage.toLowerCase().includes('expired') || 
                              couponMessage.toLowerCase().includes('not found') || 
                              couponMessage.toLowerCase().includes('not active') || 
                              couponMessage.toLowerCase().includes('belongs to') || 
                              couponMessage.toLowerCase().includes('tied to') || 
                              couponMessage.toLowerCase().includes('limit') 
                                ? 'text-red-600' 
                                : 'text-green-600'
                            }`}>{couponMessage}</p>
                          )}
                        </div>
                        
                        {/* Coupon list */}
                        {(() => {
                          const existingCoupons = Array.isArray(availableCoupons) ? availableCoupons : [];
                          const allCoupons = [...existingCoupons, ...claimedCoupons.filter(c => !existingCoupons.some(a => a.code === c.code))];
                          
                          if (allCoupons.length === 0) {
                            return (
                              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                No coupons available. Enter a code above.
                              </div>
                            );
                          }
                          
                          return (
                            <div className="max-h-48 overflow-y-auto">
                              {allCoupons.map((c) => {
                                const isSelected = couponCode === c.code;
                                const isNewlyClaimed = claimedCoupons.some(cc => cc.code === c.code) && !existingCoupons.some(a => a.code === c.code);
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => {
                                      setCouponCode(c.code);
                                      setCouponDropdownOpen(false);
                                      setCouponMessage(null);
                                    }}
                                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors ${
                                      isSelected 
                                        ? 'bg-blue-50 text-blue-900' 
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {isSelected && (
                                        <svg className="w-4 h-4 text-blue-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      <span className={isSelected ? 'font-medium' : ''}>
                                        {c.template?.name || c.code}
                                      </span>
                                      {isNewlyClaimed && (
                                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">New</span>
                                      )}
                                    </div>
                                    <span className={`font-medium shrink-0 ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                                      {c.type === 'percentage' ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Click outside to close */}
                    {couponDropdownOpen && (
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setCouponDropdownOpen(false)}
                      />
                    )}
                  </div>
                  
                  {/* Apply button */}
                  <button
                    type="button"
                    className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={async () => {
                      const selectedCode = couponCode;
                      if (!parentId || !invoiceId || !selectedCode) return;
                      try {
                        setIsApplyingCoupon(true);
                        setCouponMessage(null);
                        const { data, error } = await supabase.rpc('apply_coupon_to_invoice', {
                          p_user_id: parentId,
                          p_invoice_id: String(invoiceId),
                          p_code: selectedCode,
                        });
                        if (error) throw error;
                        const row = (data as ApplyCouponResponseRow[])[0];
                        if (!row || !row.success) {
                          setCouponMessage(row?.error || 'Failed to apply coupon');
                          setCouponDropdownOpen(true);
                        } else {
                          await (refetchInvoice?.());
                          const allCoupons = [...(Array.isArray(availableCoupons) ? availableCoupons : []), ...claimedCoupons];
                          const matched = allCoupons.find((c) => c.code === selectedCode);
                          setAppliedCouponName(matched?.template?.name || null);
                          setAppliedCoupon(row);
                          setCouponCode('');
                          setClaimedCoupons([]);
                          setCouponDropdownOpen(false);
                          await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
                          await queryClient.invalidateQueries({ queryKey: ['available-coupons'] });
                          trackEvent({ eventType: 'coupon_apply', user, entityType: 'invoice', entityId: String(invoiceId), metadata: { coupon_code: selectedCode, discount: row.discount_amount } });
                        }
                      } catch (err) {
                        setCouponMessage('Failed to apply coupon');
                      } finally {
                        setIsApplyingCoupon(false);
                      }
                    }}
                    disabled={isApplyingCoupon || !couponCode}
                  >
                    {isApplyingCoupon ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              )}
              {appliedCoupon && (
                <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="text-sm text-green-800">
                    <span className="font-medium">✓ Coupon applied:</span> {appliedCouponName ? `${appliedCouponName} — ${appliedCoupon.code}` : appliedCoupon.code}
                    <span className="text-green-600 ml-2">(-${appliedCoupon.discount_amount.toFixed(2)})</span>
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1 rounded bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 text-sm"
                    onClick={async () => {
                      if (!parentId || !invoiceId || !appliedCoupon) return;
                      try {
                        setIsApplyingCoupon(true);
                        setCouponMessage(null);
                        const { data, error } = await supabase.rpc('remove_coupon_from_invoice', {
                          p_user_id: parentId,
                          p_invoice_id: String(invoiceId),
                          p_code: appliedCoupon.code,
                        });
                        if (error) throw error;
                        const row = (data as RemoveCouponResponseRow[])[0];
                        if (!row || !row.success) {
                          setCouponMessage(row?.error || 'Failed to remove coupon');
                        } else {
                          await (refetchInvoice?.());
                          setAppliedCoupon(null);
                          setAppliedCouponName(null);
                          setCouponMessage('Coupon removed');
                          await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
                          trackEvent({ eventType: 'coupon_remove', user, entityType: 'invoice', entityId: String(invoiceId), metadata: { coupon_code: appliedCoupon.code } });
                        }
                      } catch (err) {
                        setCouponMessage('Failed to remove coupon');
                      } finally {
                        setIsApplyingCoupon(false);
                      }
                    }}
                    disabled={isApplyingCoupon}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Wallet payment UI */}
            {selectedPaymentMethod === 'wallet' && (
              <WalletTosGate parentId={parentIdStr}>
              {(() => {
              const amountDueCents = walletAmountDueCents;
              const bonusApplied = walletBonusApplied;
              const withdrawableApplied = walletWithdrawableApplied;
              const walletTotalApplied = bonusApplied + withdrawableApplied;
              const covered = walletTotalApplied >= amountDueCents;
              const remainderCents = walletRemainderCents;
              const cardFeeCents = walletSplitCardFeeCents;
              const chargedTodayCents = remainderCents + cardFeeCents;
              return (
                <div className="mb-6 space-y-4">
                  <p className="text-xs text-gray-500">
                    Bonus credit ({formatUsdFromCents(walletBalance?.non_withdrawable_cents ?? 0)}) applied first, then withdrawable balance ({formatUsdFromCents(walletBalance?.withdrawable_cents ?? 0)}).
                  </p>
                  <div className="bg-gray-50 p-4 rounded space-y-2 text-sm">
                    {bonusApplied > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Balance · Bonus credit</span>
                        <span className="font-medium">−{formatUsdFromCents(bonusApplied)}</span>
                      </div>
                    )}
                    {withdrawableApplied > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Balance · Cash</span>
                        <span className="font-medium">−{formatUsdFromCents(withdrawableApplied)}</span>
                      </div>
                    )}
                    {!covered && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {/* No fallback PM → the remainder is paid via Zelle (see instructions below),
                              so label it "Zelle" rather than the generic rail list. */}
                          {fallbackPmLabel ?? 'Zelle'}
                          {cardFeeCents > 0 && <span className="text-xs text-gray-400 ml-1">(incl. {stripeFeeLabel} fee)</span>}
                        </span>
                        <span className="font-medium">{formatUsdFromCents(chargedTodayCents)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="font-semibold">Charged today</span>
                      <span className="font-bold">{covered ? '$0.00' : formatUsdFromCents(chargedTodayCents)}</span>
                    </div>
                  </div>
                  {!covered && (
                    <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2.5">
                      {fallbackPmLabel ? (
                        <p className="text-blue-800">
                          Your BHCS Cash covers {formatUsdFromCents(walletTotalApplied)} of this invoice. The remaining{' '}
                          <span className="font-semibold">{formatUsdFromCents(chargedTodayCents)}</span>
                          {cardFeeCents > 0 ? ` (including a ${formatUsdFromCents(cardFeeCents)} processing fee)` : ''} will be charged to{' '}
                          <span className="font-semibold">{fallbackPmLabel}</span>.
                        </p>
                      ) : (
                        <p className="text-blue-800">
                          Your BHCS Cash covers {formatUsdFromCents(walletTotalApplied)}. To pay the remaining{' '}
                          <span className="font-semibold">{formatUsdFromCents(remainderCents)}</span>, send a Zelle
                          payment for exactly that amount using memo code{' '}
                          <span className="font-mono font-semibold">{invoice.memo_code}</span>. Once confirmed, the
                          remaining balance will automatically be attempted from your BHCS Cash.
                        </p>
                      )}
                      <div className="pt-1">
                        <p className="text-xs font-medium text-blue-900 mb-1.5">Pay the remainder with</p>
                        <FallbackMethodPicker
                          value={effectiveFallbackId}
                          options={fallbackOptionsList}
                          testId="checkout-fallback-select"
                          onChange={(id) => { setPickedFallbackId(id); setWalletError(null); }}
                        />
                      </div>
                    </div>
                  )}
                  {walletError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                      {walletError}
                    </div>
                  )}
                  {covered ? (
                    <button
                      type="button"
                      onClick={handleWalletPay}
                      disabled={isWalletPaying}
                      className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-md"
                    >
                      {isWalletPaying ? 'Processing…' : `${t('action.pay')} ${formatUsdFromCents(amountDueCents)} with BHCS Cash`}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      {fallbackPmLabel && (
                        <button
                          type="button"
                          onClick={handleSplitPay}
                          disabled={isWalletSplitPaying}
                          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-md"
                        >
                          {isWalletSplitPaying ? 'Processing…' : 'Confirm split payment'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setSelectedPaymentMethod('card'); setWalletError(null); }}
                        className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold rounded-md"
                      >
                        Pay full {formatUsdFromCents(amountDueCents)} by card instead
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
              </WalletTosGate>
            )}

            {selectedPaymentMethod === 'bank_account' && (
              <div className="mb-6">
                {isBankAccountLoading ? (
                  <div className="p-4 border rounded-lg bg-gray-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600 text-center">Checking bank account status...</p>
                  </div>
                ) : !bankAccountSetup ? (
                  <Elements stripe={stripePromise}>
                    <BankAccountSetup 
                      invoiceId={invoiceId} 
                      existingSetup={bankAccountSetup}
                      isLoading={isBankAccountLoading}
                    />
                  </Elements>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-3.5 border border-gray-200 rounded-xl bg-white" data-testid="bank-account-connected">
                    <div className="flex items-center gap-3 min-w-0">
                      <PaymentMethodIcon type="bank_account" size={24} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{bankAccountSetup.bank_name}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-gray-500">
                          <span className="text-gray-700">•••• {bankAccountSetup.last4} · {bankAccountSetup.account_type}</span>
                          <span className="inline-flex items-center gap-1 font-medium text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Connected
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        onClick={() => setBankMenuOpen((o) => !o)}
                        aria-label="Bank account options"
                        aria-haspopup="menu"
                        aria-expanded={bankMenuOpen}
                        data-testid="bank-account-menu"
                        className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${
                          bankMenuOpen ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                      {bankMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setBankMenuOpen(false)} aria-hidden="true" />
                          <div role="menu" className="absolute right-0 top-9 z-20 w-44 rounded-xl border border-gray-200 bg-white shadow-lg py-1 overflow-hidden">
                            <button
                              type="button"
                              role="menuitem"
                              onClick={async () => {
                                setBankMenuOpen(false);
                                try {
                                  const { error } = await supabase
                                    .from('bank_account_setup_intents')
                                    .update({ is_active: false })
                                    .eq('id', bankAccountSetup.id);
                                  if (error) throw error;
                                  // Force refresh the query
                                  window.location.reload();
                                } catch (error) {
                                  console.error('Error removing bank account:', error);
                                }
                              }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Only show PaymentForm if:
                1. It's not bank account or wallet payment, OR
                2. It's bank account payment AND bank account is connected and not loading */}
            {selectedPaymentMethod !== 'wallet' && (selectedPaymentMethod !== 'bank_account' || (selectedPaymentMethod === 'bank_account' && bankAccountSetup && !isBankAccountLoading)) && (
              <Elements stripe={stripePromise}>
                <PaymentForm 
                  amount={totalWithFee}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                  memoCode={invoice?.memo_code}
                  defaultPaymentMethod={selectedPaymentMethod}
                  invoiceId={invoiceId}
                  onChargedCardBrandChange={setChargedCardBrand}
                />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 